import React, { useEffect, useRef } from 'react';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { ChatMessage } from './ChatMessage';
import { TypingIndicator } from './TypingIndicator';
import type { Message } from '@/types/chat';

interface MessageListProps {
  messages: Message[];
  isTyping?: boolean;
  welcomeMessage?: string;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isTyping = false,
  welcomeMessage,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  return (
    <ScrollArea className="h-full w-full">
      <ScrollAreaViewport className="h-full w-full">
        <div ref={scrollRef} className="py-4 px-4 min-h-full">
        {/* Welcome Message */}
        {messages.length === 0 && welcomeMessage && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Welcome!</h3>
            <p className="text-sm text-muted-foreground max-w-xs">{welcomeMessage}</p>
          </div>
        )}

        {/* Messages */}
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {/* Typing Indicator */}
        {isTyping && <TypingIndicator />}

        {/* Scroll Anchor */}
        <div ref={bottomRef} />
        </div>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar orientation="vertical" />
    </ScrollArea>
  );
};
